import { Box, Flex, Text, Heading, Badge, Image } from "@chakra-ui/react";
import { use, useEffect, useState } from "react";
import {
  AppButton,
  AppTable,
  TableRow,
  TableCell,
  AppModal,
  AppInput,
  AppTextarea,
  AppSelect,
} from "../../components";
import { colors } from "../../theme";
import {
  PlusIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  RestaurantIcon,
} from "../../utils/svg";
import { Controller, useForm } from "react-hook-form";
import {
  useAllCountries,
  useCitiesByCountry,
  useMalls,
  useRestaurants,
  useCreateRestaurant,
  useUpdateRestaurant,
  useDeleteRestaurant,
  useSetRestaurantBlockStatus,
  useSetRestaurantApprovalStatus,
  useShopCategories,
} from "../../services/useQueries";
import { setLoading } from "../../redux/reducer/utilsSlice";
import { useDispatch } from "react-redux";
import { useAdminRole } from "../../utils/useAdminRole";
import SubscriptionPanel from "./SubscriptionPanel";
import { mediaUrl } from "../../utils/media";

const RestaurantManagementScreen = () => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [page, setPage] = useState(1);
  const [bannerFile, setBannerFile] = useState(null);
  const dispatch = useDispatch();
  // Filter states
  const { data: countries } = useAllCountries();
  const selectedCountryId = watch("filterCountry");
  const selectedCityId = watch("filterCity");
  const selectedMallId = watch("filterMall");
  const selectedCategory = watch("filterCategory");
  const { data: cities, isLoading: citiesLoading } =
    useCitiesByCountry(selectedCountryId);
  const { data: mallsData, isLoading: mallsLoading } = useMalls(selectedCityId);
  const malls = mallsData || [];

  // GAP-018: a mall admin's view is pinned to their managed mall — the
  // country/city/mall cascade is hidden and every query/create uses it.
  const { isMallAdmin, managedMallId } = useAdminRole();
  const effectiveMallId = isMallAdmin ? managedMallId : selectedMallId;

  useEffect(() => {
    dispatch(setLoading(mallsLoading));
  }, [mallsLoading]);
  // Default view shows all restaurants; filters narrow. The system-wide
  // endpoint resolves precedence: mallId > cityId > countryId.
  const { data: restaurantsData, isLoading: restaurantsLoading } =
    useRestaurants({
      page,
      limit: 10,
      mallId: effectiveMallId,
      cityId: isMallAdmin ? undefined : selectedCityId,
      countryId: isMallAdmin ? undefined : selectedCountryId,
      category: selectedCategory,
    });
  // The /restaurants/all endpoint wraps results twice:
  //   { message, data: { data: [...rows], total, page, limit, totalPages } }
  // The legacy mall-specific endpoint returned the inner shape directly:
  //   { data: [...rows], total, page, limit }
  // Support both so this code keeps working if the wrapper is ever flattened.
  const payload = restaurantsData?.data?.data
    ? restaurantsData.data        // double-wrapped (current /restaurants/all)
    : restaurantsData;             // already flat
  const restaurants = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];
  const pagination = (payload && typeof payload === "object" && !Array.isArray(payload))
    ? {
        page: payload.page ?? 1,
        totalPages: payload.totalPages ?? 1,
        total: payload.total ?? restaurants.length,
      }
    : { page: 1, totalPages: 1, total: restaurants.length };

  // Mutations
  const createRestaurantMutation = useCreateRestaurant();
  const updateRestaurantMutation = useUpdateRestaurant();
  const deleteRestaurantMutation = useDeleteRestaurant();
  const setBlockStatusMutation = useSetRestaurantBlockStatus();
  const setApprovalStatusMutation = useSetRestaurantApprovalStatus();

  // Approve / Reject — PATCH /admin/restaurants/:id/approval.
  // The customer-facing filter requires approvalStatus=APPROVED, so a
  // restaurant only goes live once the admin clicks Approve here. Restaurants
  // start as PENDING at signup; "Reject" sets them to REJECTED.
  const handleSetApproval = (restaurant, approvalStatus) => {
    const isApprove = approvalStatus === "APPROVED";
    const consequence = isApprove
      ? "The shop will be visible to customers (if its subscription is active)."
      : "The shop will be hidden from customers and won't receive orders.";
    if (!window.confirm(`${isApprove ? "Approve" : "Reject"} "${restaurant.name}"?\n\n${consequence}`)) {
      return;
    }
    setApprovalStatusMutation.mutate({
      id: restaurant.userId || restaurant.id,
      approvalStatus,
      reason: `Admin set approval to ${approvalStatus} via dashboard`,
    });
  };

  // Block / unblock — confirmation, then PATCH /admin/restaurants/:id/block.
  // Backend sets RestaurantStatus to BLOCKED/ACTIVE and the customer-facing
  // reads hide the restaurant the moment it's blocked.
  const handleToggleBlock = (restaurant) => {
    const isBlocked = restaurant.RestaurantStatus === "BLOCKED";
    const verb = isBlocked ? "Unblock" : "Block";
    const consequence = isBlocked
      ? "Customers will be able to see this shop again."
      : "The shop will be hidden from the customer app and won't receive new orders.";
    if (!window.confirm(`${verb} "${restaurant.name}"?\n\n${consequence}`)) {
      return;
    }
    setBlockStatusMutation.mutate({
      id: restaurant.userId || restaurant.id,
      isBlocked: !isBlocked,
      reason: `Admin ${verb.toLowerCase()} via dashboard`,
    });
  };
  // Shop categories: the same list the buyer and shop apps use (food and
  // drink, and shopping). Buyers find a shop under each one it has.
  const { data: categoriesData, isLoading: categoriesLoading } = useShopCategories();
  const categoryGroups = Array.isArray(categoriesData?.groups) ? categoriesData.groups : [];
  const categoryOptions = (Array.isArray(categoriesData?.cuisines) ? categoriesData.cuisines : []).map(
    (c) => ({ value: c, label: c })
  );

  const headers = [
    "Shop",
    "Address",
    "Assign Mall",
    "Status",
    "Approval",
    "Membership Plan",
    "Actions",
  ];

  const handleAddRestaurant = () => {
    setSelectedRestaurant(null);
    setBannerFile(null);
    reset({
      // Preserve filter values
      filterCountry: selectedCountryId,
      filterCity: selectedCityId,
      filterMall: selectedMallId,
      filterCategory: selectedCategory,
      // Modal form fields
      name: "",
      description: "",
      story: "",
      location: "",
      cuisines: [],
      // The filtered mall, or the only mall there is; otherwise pick one.
      mallId: effectiveMallId || (malls.length === 1 ? malls[0].id : ""),
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      password: "",
      isFavorite: false,
    });
    setIsModalOpen(true);
  };

  const handleEditRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setBannerFile(null);
    reset({
      // Preserve filter values
      filterCountry: selectedCountryId,
      filterCity: selectedCityId,
      filterMall: selectedMallId,
      filterCategory: selectedCategory,
      // Modal form fields
      name: restaurant.name || "",
      description: restaurant.description || "",
      story: restaurant.story || "",
      location: restaurant.location || "",
      cuisines: Array.isArray(restaurant.cuisines) ? restaurant.cuisines : [],
      mallId: restaurant.mallId || effectiveMallId || "",
      isFavorite: restaurant.isFavorite || false,
    });
    setIsModalOpen(true);
  };

  const handleViewRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsViewModalOpen(true);
  };

  const handleDeleteRestaurant = (restaurant) => {
    if (!restaurant.userId) return;
    if (
      window.confirm(
        "Delete this shop? Only a shop that has never received an order can be deleted. To take a shop with orders off MallBuddy, block it instead."
      )
    ) {
      deleteRestaurantMutation.mutate(restaurant.userId);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
    }
  };

  const onSubmit = (data) => {
    const formData = new FormData();

    // Common fields for both create and update
    formData.append("name", data.name);
    formData.append("description", data.description || "");
    formData.append("story", data.story || "");
    formData.append("location", data.location || "");
    const categories = Array.isArray(data.cuisines) ? data.cuisines : [];
    formData.append("cuisines", JSON.stringify(categories));
    // Older screens read a single main category: the first one.
    formData.append("mainCategory", categories[0] || "");
    formData.append("isFavorite", data.isFavorite || false);

    if (data.mallId) formData.append("mallId", data.mallId);

    // Add banner if selected
    if (bannerFile) {
      formData.append("banner", bannerFile);
    }

    const editingId =
      selectedRestaurant?.userId || selectedRestaurant?.restaurantId || selectedRestaurant?.id;
    if (editingId) {
      // Update restaurant
      updateRestaurantMutation.mutate(
        { id: editingId, data: formData },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            // Preserve filter values when resetting
            reset({
              filterCountry: selectedCountryId,
              filterCity: selectedCityId,
              filterMall: selectedMallId,
              filterCategory: selectedCategory,
            });
            setBannerFile(null);
          },
        }
      );
    } else {
      // Create restaurant - additional fields required
      formData.append("firstName", data.firstName || "");
      formData.append("lastName", data.lastName || "");
      formData.append("email", data.email || "");
      formData.append("phoneNumber", data.phoneNumber || "");
      formData.append("password", data.password || "");

      createRestaurantMutation.mutate(formData, {
        onSuccess: () => {
          setIsModalOpen(false);
          // Preserve filter values when resetting
          reset({
            filterCountry: selectedCountryId,
            filterCity: selectedCityId,
            filterMall: selectedMallId,
            filterCategory: selectedCategory,
          });
          setBannerFile(null);
        },
      });
    }
  };

  const renderRow = (restaurant, index) => (
    <TableRow key={restaurant.id || index}>
      <TableCell fontWeight="500">
        <Flex align="center" gap={2}>
          {restaurant.name}
        </Flex>
      </TableCell>
      <TableCell>{restaurant.location}</TableCell>
      <TableCell>
        <Badge
          colorPalette="blue"
          px={2}
          py={1}
          borderRadius="full"
          fontSize="xs"
        >
          {restaurant?.mall?.name || "N/A"}
        </Badge>
      </TableCell>
      <TableCell>
        {" "}
        <Badge
          colorPalette={
            restaurant.RestaurantStatus === "ACTIVE" ? "green" : "red"
          }
          px={2}
          py={1}
          borderRadius="full"
          fontSize="xs"
        >
          {restaurant.RestaurantStatus || "N/A"}{" "}
        </Badge>
      </TableCell>
      <TableCell>
        {/* approvalStatus drives whether the customer app even sees this
            restaurant — see activeSubscriptionWhere() on the backend. */}
        <Badge
          colorPalette={
            restaurant.approvalStatus === "APPROVED"
              ? "green"
              : restaurant.approvalStatus === "REJECTED"
              ? "red"
              : "orange"
          }
          px={2}
          py={1}
          borderRadius="full"
          fontSize="xs"
        >
          {restaurant.approvalStatus || "PENDING"}
        </Badge>
      </TableCell>
      <TableCell>
        {/* Membership plan column — backend returns the name of the
            currently-ACTIVE subscription plan (or null when there's none).
            Was previously rendering `isFavorite` here, which is why every
            row showed "No" even after a plan was set. */}
        <Badge
          colorPalette={restaurant.membershipPlan ? "purple" : "gray"}
          px={2}
          py={1}
          borderRadius="full"
          fontSize="xs"
        >
          {restaurant.membershipPlan || "None"}
        </Badge>
      </TableCell>
      <TableCell>
        <Flex gap={2}>
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleViewRestaurant(restaurant)}
            leftIcon={<ViewIcon boxSize={4} />}
          />
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleEditRestaurant(restaurant)}
            leftIcon={<EditIcon boxSize={4} />}
          />
          {/* Approve / Reject — appears for anything not already APPROVED.
              For PENDING rows we show both buttons; for REJECTED rows we
              show Approve so the admin can recover a mistaken reject. */}
          {restaurant.approvalStatus !== "APPROVED" && (
            <AppButton
              variant="primary"
              size="sm"
              onClick={() => handleSetApproval(restaurant, "APPROVED")}
              disabled={setApprovalStatusMutation.isPending}
            >
              Approve
            </AppButton>
          )}
          {restaurant.approvalStatus === "PENDING" && (
            <AppButton
              variant="secondary"
              size="sm"
              onClick={() => handleSetApproval(restaurant, "REJECTED")}
              disabled={setApprovalStatusMutation.isPending}
            >
              Reject
            </AppButton>
          )}

          {/* Block / Unblock — label flips based on current RestaurantStatus.
              While the request is in flight, disable the button so a fast
              double-click can't fire two opposite mutations back-to-back. */}
          <AppButton
            variant={
              restaurant.RestaurantStatus === "BLOCKED" ? "secondary" : "ghost"
            }
            size="sm"
            onClick={() => handleToggleBlock(restaurant)}
            disabled={setBlockStatusMutation.isPending}
          >
            {restaurant.RestaurantStatus === "BLOCKED" ? "Unblock" : "Block"}
          </AppButton>
          <AppButton
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteRestaurant(restaurant)}
            leftIcon={<DeleteIcon boxSize={4} color="#EF4444" />}
          />
        </Flex>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color={colors.text} mb={1}>
            Shops
          </Heading>
          <Text color={colors.textSecondary}>
            Every shop on MallBuddy: add, edit, approve or block
          </Text>
        </Box>
        <AppButton
          variant="primary"
          leftIcon={<RestaurantIcon boxSize={5} />}
          onClick={handleAddRestaurant}
        >
          Add a shop
        </AppButton>
      </Flex>

      {/* Filters — a mall admin is pinned to their mall (GAP-018), so the
          country/city/mall cascade is hidden for them; category remains. */}
      <Flex gap={3} mb={6} flexWrap="wrap">
        {!isMallAdmin && (
          <>
            <Controller
              control={control}
              name="filterCountry"
              render={({ field }) => (
                <AppSelect
                  placeholder="Select Country"
                  options={
                    countries?.map((c) => ({ value: c.id, label: c.name })) || []
                  }
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue("filterCity", undefined);
                    setValue("filterMall", undefined);
                  }}
                  width="180px"
                />
              )}
            />
            <Controller
              control={control}
              name="filterCity"
              render={({ field }) => (
                <AppSelect
                  placeholder={citiesLoading ? "Loading..." : "Select City"}
                  options={
                    cities?.map((c) => ({ value: c.id, label: c.name })) || []
                  }
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(val);
                    setValue("filterMall", undefined);
                  }}
                  disabled={!selectedCountryId || citiesLoading}
                  width="180px"
                />
              )}
            />
            <Controller
              control={control}
              name="filterMall"
              render={({ field }) => (
                <AppSelect
                  placeholder={mallsLoading ? "Loading..." : "Select Mall"}
                  options={
                    malls?.map((m) => ({ value: m.id, label: m.name })) || []
                  }
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!selectedCityId || mallsLoading}
                  width="180px"
                />
              )}
            />
          </>
        )}
        <Controller
          control={control}
          name="filterCategory"
          render={({ field }) => (
            <AppSelect
              placeholder={categoriesLoading ? "Loading..." : "Filter by category"}
              options={categoryOptions}
              value={field.value}
              onChange={field.onChange}
              width="180px"
            />
          )}
        />

        {/* Clear Filter — only renders when at least one of the four
            filters is set. Reset to undefined (the empty state). */}
        {(selectedCountryId ||
          selectedCityId ||
          selectedMallId ||
          selectedCategory) && (
          <AppButton
            variant="secondary"
            onClick={() => {
              setValue("filterCountry", undefined);
              setValue("filterCity", undefined);
              setValue("filterMall", undefined);
              setValue("filterCategory", undefined);
            }}
          >
            Clear Filter
          </AppButton>
        )}
      </Flex>

      {/* The default view returns every restaurant via /restaurants/all,
          so the old "please pick country/city/mall" gate is no longer needed.
          Filters above the table narrow this list. */}

      {/* Table */}
      <AppTable
        headers={headers}
        data={restaurants}
        renderRow={renderRow}
        loading={restaurantsLoading}
        pagination={{
          page: pagination.page || page,
          totalPages: pagination.totalPages || 1,
          onPageChange: setPage,
        }}
      />

      {/* Add/Edit Modal */}
      <AppModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setBannerFile(null);
        }}
        title={selectedRestaurant ? "Edit shop" : "Add a shop"}
        size="xl"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setBannerFile(null);
              }}
            >
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              onClick={handleSubmit(onSubmit)}
              isLoading={
                createRestaurantMutation.isPending ||
                updateRestaurantMutation.isPending
              }
            >
              {selectedRestaurant ? "Save changes" : "Add shop"}
            </AppButton>
          </Flex>
        }
      >
        <Flex direction="column" gap={4}>
          <AppInput
            label="Shop name"
            name="name"
            placeholder="e.g. Karak & Co."
            register={register}
            errors={errors}
            required
          />

          <Flex gap={4}>
            <Box flex={1}>
              <Controller
                control={control}
                name="mallId"
                rules={{ required: "Mall is required" }}
                render={({ field }) => (
                  <AppSelect
                    label="Mall"
                    placeholder="Select mall"
                    options={
                      // A mall admin's locked select only lists their own mall
                      // (don't enumerate other malls' names in a disabled control).
                      (isMallAdmin
                        ? (malls || []).filter((m) => m.id === managedMallId)
                        : malls || []
                      ).map((m) => ({ value: m.id, label: m.name }))
                    }
                    value={field.value}
                    onChange={field.onChange}
                    // A mall admin can only create restaurants in their own
                    // mall; the backend forces this regardless (GAP-018).
                    disabled={isMallAdmin}
                    error={errors.mallId}
                  />
                )}
              />
            </Box>
          </Flex>

          <Controller
            control={control}
            name="cuisines"
            rules={{
              validate: (v) =>
                (Array.isArray(v) && v.length > 0) || "Pick at least one category",
            }}
            render={({ field }) => {
              const picked = Array.isArray(field.value) ? field.value : [];
              const toggle = (c) =>
                field.onChange(
                  picked.includes(c) ? picked.filter((x) => x !== c) : [...picked, c]
                );
              return (
                <Box>
                  <Flex mb={1.5} align="center">
                    <Text fontSize="sm" fontWeight="500" color={colors.text}>
                      Categories
                    </Text>
                    <Text color={colors.primary} ml={1}>
                      *
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color={colors.textSecondary} mb={2}>
                    Buyers find the shop under each category picked here.
                  </Text>
                  {categoriesLoading && (
                    <Text fontSize="sm" color={colors.textSecondary}>
                      Loading...
                    </Text>
                  )}
                  {categoryGroups.map((group) => (
                    <Box key={group.name} mb={3}>
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                        color={colors.textSecondary}
                        mb={1.5}
                      >
                        {group.name}
                      </Text>
                      <Flex wrap="wrap" gap={2}>
                        {group.categories.map((c) => {
                          const on = picked.includes(c);
                          return (
                            <button
                              key={c}
                              type="button"
                              aria-pressed={on}
                              onClick={() => toggle(c)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: "999px",
                                fontSize: "13px",
                                cursor: "pointer",
                                border: `1px solid ${on ? colors.primary : "#E2E8F0"}`,
                                background: on ? colors.primary : "white",
                                color: on ? "white" : colors.text,
                              }}
                            >
                              {c}
                            </button>
                          );
                        })}
                      </Flex>
                    </Box>
                  ))}
                  {errors.cuisines && (
                    <Text fontSize="sm" color={colors.primary} mt={1}>
                      {errors.cuisines.message}
                    </Text>
                  )}
                </Box>
              );
            }}
          />

          <AppInput
            label="Where it is in the mall"
            name="location"
            placeholder="e.g. Ground floor, next to the main entrance"
            register={register}
            errors={errors}
          />

          <AppTextarea
            label="Description"
            name="description"
            placeholder="What the shop sells, in a sentence or two"
            register={register}
            errors={errors}
            rows={3}
          />

          <AppTextarea
            label="Story"
            name="story"
            placeholder="The shop's story (optional)"
            register={register}
            errors={errors}
            rows={2}
          />

          {/* Banner Upload */}
          <Box>
            <Text mb={2} fontWeight="500" color={colors.text}>
              Banner Image
            </Text>
            <input
              type="file"
              accept="image/*"
              onChange={handleBannerChange}
              style={{
                padding: "8px",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                width: "100%",
              }}
            />
            {bannerFile && (
              <Text fontSize="sm" color="green.500" mt={1}>
                Selected: {bannerFile.name}
              </Text>
            )}
          </Box>

          <Controller
            control={control}
            name="isFavorite"
            render={({ field }) => (
              <Flex align="center" gap={2}>
                <input
                  type="checkbox"
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                  id="isFavorite"
                />
                <label htmlFor="isFavorite">
                  <Text color={colors.text}>Mark as Favorite</Text>
                </label>
              </Flex>
            )}
          />

          {/* Owner Info - Only for Create */}
          {!selectedRestaurant && (
            <>
              <Box borderTop="1px solid" borderColor="gray.200" pt={4} mt={2}>
                <Heading size="sm" mb={4} color={colors.text}>
                  The shop's login (for the MallBuddy shop app)
                </Heading>
              </Box>

              <Flex gap={4}>
                <AppInput
                  label="First Name"
                  name="firstName"
                  placeholder="Enter first name"
                  register={register}
                  errors={errors}
                  required
                />
                <AppInput
                  label="Last Name"
                  name="lastName"
                  placeholder="Enter last name"
                  register={register}
                  errors={errors}
                  required
                />
              </Flex>

              <Flex gap={4}>
                <AppInput
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="e.g. owner@shop.com"
                  register={register}
                  errors={errors}
                  required
                />
                <AppInput
                  label="Phone Number"
                  name="phoneNumber"
                  placeholder="e.g. +968 9123 4567"
                  register={register}
                  errors={errors}
                  required
                />
              </Flex>

              <AppInput
                label="Password"
                name="password"
                type="password"
                placeholder="Enter password (min 8 characters)"
                register={register}
                errors={errors}
                required
              />
            </>
          )}
        </Flex>
      </AppModal>

      {/* View Details Modal */}
      <AppModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="Shop details"
        size="lg"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton
              variant="secondary"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </AppButton>
          </Flex>
        }
      >
        {selectedRestaurant && (
          <Flex direction="column" gap={4}>
            {selectedRestaurant.banner && (
              <Image
                src={mediaUrl(selectedRestaurant.banner)}
                alt={selectedRestaurant.name}
                w="100%"
                maxH="200px"
                objectFit="cover"
                borderRadius="md"
              />
            )}
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Name
              </Text>
              <Text color={colors.textSecondary}>
                {selectedRestaurant.name}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Location
              </Text>
              <Text color={colors.textSecondary}>
                {selectedRestaurant.location || "N/A"}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Categories
              </Text>
              <Text color={colors.textSecondary}>
                {Array.isArray(selectedRestaurant.cuisines) && selectedRestaurant.cuisines.length
                  ? selectedRestaurant.cuisines.join(", ")
                  : selectedRestaurant.mainCategory || "None yet"}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Description
              </Text>
              <Text color={colors.textSecondary}>
                {selectedRestaurant.description || "N/A"}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Story
              </Text>
              <Text color={colors.textSecondary}>
                {selectedRestaurant.story || "N/A"}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Contact
              </Text>
              <Text color={colors.textSecondary}>
                {selectedRestaurant.phoneNumber || "N/A"}
              </Text>
            </Box>
            <Box>
              <Text fontWeight="bold" color={colors.text}>
                Favorite
              </Text>
              <Badge
                colorPalette={selectedRestaurant.isFavorite ? "green" : "gray"}
              >
                {selectedRestaurant.isFavorite ? "Yes" : "No"}
              </Badge>
            </Box>
            {/* Subscriptions are managed by super admins only (the API refuses mall admins). */}
            {!isMallAdmin && (
              <SubscriptionPanel
                restaurantId={
                  selectedRestaurant.userId ||
                  selectedRestaurant.restaurantId ||
                  selectedRestaurant.id
                }
              />
            )}
          </Flex>
        )}
      </AppModal>
    </Box>
  );
};

export default RestaurantManagementScreen;
